import { useState, useEffect, useMemo } from 'react';
import { atlasApiService } from '../services/atlasApi';
import { ClusterInfo } from '../types/atlas';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useAuth } from '../contexts/AuthContext';
import { clusterManagementService } from '../services/clusterManagementService';
import { AlertCircle, Loader, Database, HardDrive, FileText, Clock, Search } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useIsMobile } from '../hooks/useIsMobile';
import { PageHeader } from '../components/ui/PageHeader';
import { StatTile } from '../components/ui/StatTile';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
};

const formatNumber = (num: number): string => {
  return num.toLocaleString();
};

const PAGE_DESCRIPTION =
  'View statistics and storage information for modpack assets across all clusters.';

/** Compact icon + label / value metric row used inside cluster cards. */
const MetricRow = ({
  icon: Icon,
  iconClassName,
  label,
  value,
  sub,
}: {
  icon: React.ComponentType<{ className?: string }>;
  iconClassName: string;
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
}) => (
  <div className="flex items-center justify-between gap-3 py-2 text-xs">
    <span className="flex items-center gap-2 text-glass-400 min-w-0">
      <Icon className={`w-3.5 h-3.5 shrink-0 ${iconClassName}`} />
      <span className="truncate">{label}</span>
    </span>
    <span className="text-right min-w-0">
      <span className="block text-white font-medium tabular-nums truncate">{value}</span>
      {sub && <span className="block text-glass-500 tabular-nums mt-0.5">{sub}</span>}
    </span>
  </div>
);

interface ClusterCardProps {
  cluster: ClusterInfo;
  clusterName: string | null;
  isLoadingName: boolean;
}

const ClusterCard = ({ cluster, clusterName, isLoadingName }: ClusterCardProps) => {
  const displayName = clusterName || cluster.cluster_id;
  const isFetchingName = isLoadingName && !clusterName;
  const lastUpdated = cluster.last_updated 
    ? new Date(cluster.last_updated) 
    : null;

  return (
    <div className="glass-card p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="icon-tile icon-tile-accent">
            <Database className="w-4 h-4" />
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-white truncate">
              {isFetchingName ? 'Loading...' : displayName}
            </h3>
            {clusterName && clusterName !== cluster.cluster_id && (
              <p className="text-xs text-glass-400 font-mono truncate">{cluster.cluster_id}</p>
            )}
            {isFetchingName && (
              <p className="text-xs text-glass-400 font-mono truncate">{cluster.cluster_id}</p>
            )}
          </div>
        </div>
      </div>

      <div className="divide-y divide-glass-200/10">
        <MetricRow icon={FileText} iconClassName="text-blue-400" label="Asset Count" value={formatNumber(cluster.asset_count)} />
        <MetricRow icon={HardDrive} iconClassName="text-green-400" label="Storage Size" value={formatBytes(cluster.storage_size_bytes)} />
        <MetricRow icon={Database} iconClassName="text-purple-400" label="Object Count" value={formatNumber(cluster.object_count)} />
        <MetricRow
          icon={Clock}
          iconClassName="text-yellow-400"
          label="Last Updated"
          value={lastUpdated 
            ? formatDistanceToNow(lastUpdated, { addSuffix: true })
            : 'Never'}
          sub={lastUpdated ? lastUpdated.toLocaleString() : undefined}
        />
      </div>

      {/* Storage efficiency indicator */}
      {cluster.asset_count > 0 && (
        <div className="mt-3 pt-3 border-t border-glass-200/10">
          <div className="flex items-center justify-between text-xs">
            <span className="text-glass-400">Avg. Size per Asset</span>
            <span className="text-white font-medium tabular-nums">
              {formatBytes(cluster.storage_size_bytes / cluster.asset_count)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

interface DesktopClusterCardProps {
  cluster: ClusterInfo;
  clusterName: string | null;
  isLoadingName: boolean;
}

const DesktopClusterCard = ({ cluster, clusterName, isLoadingName }: DesktopClusterCardProps) => {
  const displayName = clusterName || cluster.cluster_id;
  const isFetchingName = isLoadingName && !clusterName;
  const lastUpdated = cluster.last_updated 
    ? new Date(cluster.last_updated) 
    : null;
  const avgSizePerAsset = cluster.asset_count > 0 
    ? cluster.storage_size_bytes / cluster.asset_count 
    : 0;

  return (
    <div className="glass-card p-4 md:p-5 h-full flex flex-col">
      {/* Header with icon and cluster name */}
      <div className="flex items-start gap-3 mb-3">
        <span className="icon-tile icon-tile-accent">
          <Database className="w-4 h-4" />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-white truncate">
            {isFetchingName ? 'Loading...' : displayName}
          </h3>
          {(clusterName && clusterName !== cluster.cluster_id) || isFetchingName ? (
            <p className="text-xs text-glass-400 font-mono truncate mt-0.5">{cluster.cluster_id}</p>
          ) : null}
        </div>
      </div>

      {/* Stats rows */}
      <div className="divide-y divide-glass-200/10 flex-1">
        <MetricRow icon={FileText} iconClassName="text-blue-400" label="Assets" value={formatNumber(cluster.asset_count)} />
        <MetricRow icon={Database} iconClassName="text-purple-400" label="Objects" value={formatNumber(cluster.object_count)} />
        <MetricRow icon={HardDrive} iconClassName="text-green-400" label="Storage" value={formatBytes(cluster.storage_size_bytes)} />
        <MetricRow
          icon={Clock}
          iconClassName="text-yellow-400"
          label="Updated"
          value={lastUpdated 
            ? formatDistanceToNow(lastUpdated, { addSuffix: true })
            : 'Never'}
          sub={lastUpdated ? lastUpdated.toLocaleDateString() : undefined}
        />
      </div>

      {/* Footer with average size */}
      {cluster.asset_count > 0 && (
        <div className="mt-3 pt-3 border-t border-glass-200/10">
          <div className="flex items-center justify-between text-xs">
            <span className="text-glass-400">Avg. Size/Asset</span>
            <span className="text-white font-medium tabular-nums">{formatBytes(avgSizePerAsset)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

/** Layout-mirroring skeleton for the cluster card grid. */
const ClusterGridSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" aria-busy="true">
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className="glass-card p-4 md:p-5">
        <div className="flex items-start gap-3 mb-3">
          <SkeletonLoader className="w-9 h-9 rounded-lg shrink-0" />
          <div className="flex-1 space-y-2">
            <SkeletonLoader className="h-4 w-2/3" />
            <SkeletonLoader className="h-3 w-1/2" />
          </div>
        </div>
        <div className="space-y-3">
          <SkeletonLoader className="h-3 w-full" />
          <SkeletonLoader className="h-3 w-5/6" />
          <SkeletonLoader className="h-3 w-4/6" />
          <SkeletonLoader className="h-3 w-5/6" />
        </div>
      </div>
    ))}
  </div>
);

export const AtlasView = () => {
  useDocumentTitle('Atlas CDN');
  const { identity } = useAuth();
  const isMobile = useIsMobile();
  
  const [clusters, setClusters] = useState<ClusterInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [clusterNames, setClusterNames] = useState<Record<string, string>>({});
  const [loadingNames, setLoadingNames] = useState<Set<string>>(new Set());

  const canViewAtlas = useMemo(() => {
    if (import.meta.env.DEV) return true;
    if (!identity) return false;
    const permissions = identity.raw.custom.permissions || [];
    return permissions.includes('argus.atlas.read');
  }, [identity]);

  useEffect(() => {
    if (!canViewAtlas) {
      setError('You do not have permission to view Atlas CDN statistics.');
      setLoading(false);
      return;
    }

    fetchClusters();
  }, [canViewAtlas]);

  const fetchClusters = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await atlasApiService.getClusters();
      // Filter out the "api" cluster
      const filteredClusters = (response.clusters || []).filter(cluster => cluster.cluster_id !== 'api');
      setClusters(filteredClusters);
      
      // Fetch cluster names for all clusters
      const namePromises = filteredClusters.map(async (cluster) => {
        setLoadingNames(prev => new Set(prev).add(cluster.cluster_id));
        try {
          // Get cluster mapping to check if cluster_id matches a cluster name
          const mapping = await clusterManagementService.getClusterFqdnMapping();
          
          // If cluster_id is already a cluster name (exists as key in mapping), use it directly
          if (mapping[cluster.cluster_id]) {
            // It's already a cluster name, set it so we display it properly
            setClusterNames(prev => ({ ...prev, [cluster.cluster_id]: cluster.cluster_id }));
          } else {
            // Try to resolve it as an FQDN
            const name = await clusterManagementService.getClusterName(cluster.cluster_id);
            if (name !== cluster.cluster_id) {
              setClusterNames(prev => ({ ...prev, [cluster.cluster_id]: name }));
            }
          }
        } catch (err) {
          // If lookup fails, cluster name will remain as cluster_id
          console.warn(`Failed to get cluster name for ${cluster.cluster_id}:`, err);
        } finally {
          setLoadingNames(prev => {
            const next = new Set(prev);
            next.delete(cluster.cluster_id);
            return next;
          });
        }
      });
      
      await Promise.all(namePromises);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load Atlas CDN statistics';
      setError(errorMessage);
      setClusters([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredClusters = useMemo(() => {
    if (!searchQuery.trim()) return clusters;
    
    const query = searchQuery.toLowerCase();
    return clusters.filter(cluster => {
      const clusterId = cluster.cluster_id.toLowerCase();
      const clusterName = clusterNames[cluster.cluster_id]?.toLowerCase() || '';
      return clusterId.includes(query) || clusterName.includes(query);
    });
  }, [clusters, searchQuery, clusterNames]);

  const totalStats = useMemo(() => {
    return filteredClusters.reduce(
      (acc, cluster) => ({
        totalAssets: acc.totalAssets + cluster.asset_count,
        totalStorage: acc.totalStorage + cluster.storage_size_bytes,
        totalObjects: acc.totalObjects + cluster.object_count,
      }),
      { totalAssets: 0, totalStorage: 0, totalObjects: 0 }
    );
  }, [filteredClusters]);

  const renderMobileView = () => {
    return (
      <div className="space-y-3">
        {filteredClusters.map((cluster) => (
          <ClusterCard
            key={cluster.cluster_id}
            cluster={cluster}
            clusterName={clusterNames[cluster.cluster_id] || null}
            isLoadingName={loadingNames.has(cluster.cluster_id)}
          />
        ))}
      </div>
    );
  };

  const renderDesktopView = () => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredClusters.map((cluster) => (
          <DesktopClusterCard
            key={cluster.cluster_id}
            cluster={cluster}
            clusterName={clusterNames[cluster.cluster_id] || null}
            isLoadingName={loadingNames.has(cluster.cluster_id)}
          />
        ))}
      </div>
    );
  };

  if (!canViewAtlas) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Infrastructure" title="Atlas CDN" description={PAGE_DESCRIPTION} />
        <EmptyState
          tone="danger"
          icon={AlertCircle}
          title="Access Denied"
          description={
            <>
              You do not have permission to view Atlas CDN statistics.{' '}
              Requires <code className="font-mono text-xs bg-dark-900/50 px-1 py-0.5 rounded">argus.atlas.read</code> permission.
            </>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        eyebrow="Infrastructure"
        title="Atlas CDN"
        description={PAGE_DESCRIPTION}
      />

      {/* Toolbar */}
      <div className="glass-card p-3 flex flex-col md:flex-row md:items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="glass-input w-full pl-9"
            placeholder="Search clusters by ID or name..."
          />
        </div>
        <span className="flex items-center gap-1.5 text-xs text-glass-400 tabular-nums md:pr-1">
          {loading ? (
            <>
              <Loader className="w-3.5 h-3.5 animate-spin" />
              Loading cluster statistics...
            </>
          ) : (
            <>
              {filteredClusters.length} of {clusters.length} cluster{clusters.length === 1 ? '' : 's'}
            </>
          )}
        </span>
      </div>

      {/* Summary Stats */}
      {!loading && filteredClusters.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <StatTile label="Total Assets" icon={FileText} tone="info" value={formatNumber(totalStats.totalAssets)} />
          <StatTile label="Total Storage" icon={HardDrive} tone="success" value={formatBytes(totalStats.totalStorage)} />
          <StatTile label="Total Objects" icon={Database} tone="purple" value={formatNumber(totalStats.totalObjects)} />
        </div>
      )}

      {/* Error State */}
      {error && (
        <EmptyState
          tone="danger"
          icon={AlertCircle}
          title="Error Loading Atlas Statistics"
          description={error}
          action={
            <button type="button" onClick={() => void fetchClusters()} className="glass-button">
              Retry
            </button>
          }
        />
      )}

      {/* Loading State */}
      {loading && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[0, 1, 2].map((i) => (
              <StatTile key={i} label="" value="" loading />
            ))}
          </div>
          <ClusterGridSkeleton />
        </>
      )}

      {/* Clusters List */}
      {!loading && (
        <>
          {filteredClusters.length === 0 ? (
            <EmptyState
              icon={Database}
              title={searchQuery 
                ? 'No clusters match your search query' 
                : 'No clusters found in Atlas CDN'}
            />
          ) : (
            isMobile ? renderMobileView() : renderDesktopView()
          )}
        </>
      )}
    </div>
  );
};
